import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import CopyableInput from './CopyableInput';
import { enableSharing, disableSharing } from '../../../firebase/sharing';

const ShareModal = () => {
  const { t } = useTranslation();

  // TODO: store these as nested properties instead of top-level
  const projectId = useSelector((state) => state.ide.shareModalProjectId);
  const projectName = useSelector((state) => state.ide.shareModalProjectName);
  const ownerUsername = useSelector(
    (state) => state.ide.shareModalProjectUsername
  );

  const project = useSelector((state) => state.project);
  const userId = useSelector((state) => state.user.id);
  // Capability share links can only be created for your OWN, currently-open sketch.
  const isOwnOpenSketch =
    project.id === projectId && project.owner && project.owner.id === userId;

  const [tokens, setTokens] = useState(null); // { viewToken, editToken }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const hostname = window.location.origin;
  const sketchUrl = (token) =>
    `${hostname}/${ownerUsername}/sketches/${projectId}?token=${token}`;

  // Generate (or fetch the existing — enableSharing is idempotent) view/edit links on open, so they
  // show directly like the other links instead of behind a button.
  useEffect(() => {
    let cancelled = false;
    if (isOwnOpenSketch) {
      setBusy(true);
      setError(null);
      enableSharing(projectId)
        .then((t2) => {
          if (!cancelled) setTokens(t2);
        })
        .catch(() => {
          if (!cancelled)
            setError(
              t('ShareModal.ShareLinksError', {
                defaultValue: 'Could not load the share links.'
              })
            );
        })
        .finally(() => {
          if (!cancelled) setBusy(false);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [isOwnOpenSketch, projectId]);

  // Revoke the current links and mint fresh ones (old links stop working).
  const handleRegenerate = async () => {
    setBusy(true);
    setError(null);
    try {
      await disableSharing(projectId);
      setTokens(await enableSharing(projectId));
    } catch (e) {
      setError(
        t('ShareModal.ShareLinksError', {
          defaultValue: 'Could not regenerate the share links.'
        })
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="share-modal">
      <h3 className="share-modal__project-name">{projectName}</h3>
      <CopyableInput
        label={t('ShareModal.Embed')}
        value={`<iframe src="${hostname}/${ownerUsername}/full/${projectId}"></iframe>`}
      />
      <CopyableInput
        label={t('ShareModal.Fullscreen')}
        hasPreviewLink
        value={`${hostname}/${ownerUsername}/full/${projectId}`}
      />

      {isOwnOpenSketch && tokens && (
        <>
          <CopyableInput
            label={t('ShareModal.ViewLink', {
              defaultValue: 'View link (read-only)'
            })}
            hasPreviewLink
            value={sketchUrl(tokens.viewToken)}
          />
          <CopyableInput
            label={t('ShareModal.EditLink', {
              defaultValue: 'Edit link (can make changes)'
            })}
            hasPreviewLink
            value={sketchUrl(tokens.editToken)}
          />
          <p className="share-modal__regenerate">
            <button type="button" onClick={handleRegenerate} disabled={busy}>
              {t('ShareModal.RegenerateLinks', {
                defaultValue: 'Regenerate links (revoke the current ones)'
              })}
            </button>
          </p>
        </>
      )}
      {isOwnOpenSketch && busy && !tokens && (
        <p className="share-modal__loading">
          {t('ShareModal.GeneratingLinks', {
            defaultValue: 'Generating share links…'
          })}
        </p>
      )}
      {error && <p className="share-modal__collab-error">{error}</p>}
    </div>
  );
};

export default ShareModal;
